import { CurrencyFormat, DateTimeFormat } from '@paypalcorp/worldready'
import { DataTableUtils } from '@paypalcorp/pp-react'
import moment from 'moment'
import { decryptClientId } from './app-utils'
import serverData from './server-data'

/**
 * Sorts a copy of `rows` by the given column/direction using the shared
 * DataTable comparator. Pure: the input array is not mutated.
 *
 * Use this for one-shot sorts where only the row order needs to change and the
 * column-header metadata should stay as-is (e.g. pre-sorting rows on initial
 * load). When the active sort column/direction itself is changing, use
 * `sortRowsAndUpdateColumns` instead.
 *
 * @param {Array}  rows      the rows to sort (not mutated)
 * @param {string} name      name of the column to sort by
 * @param {string} direction 'ascending' | 'descending'
 * @returns {Array} a new, sorted array
 */
export function sortRows(rows, name, direction) {
  return [...rows].sort((a, b) =>
    DataTableUtils.sortCompare(a, b, name, direction),
  )
}

/**
 * Shared sort logic for DataTable-based components.
 *
 * Sorts `rows` by the given column/direction and returns updated column
 * metadata (sort direction + accessible sort-button labels + the
 * `isSortCurrent` flag) so the column header reflects the active sort.
 *
 * This is a pure function: it reads `columns`/`sortedByColumn` but never
 * mutates them and performs no state updates. Callers apply the returned
 * values with their own state setters, which lets components with different
 * backing arrays (e.g. `filteredRows` vs `records`) share one implementation.
 *
 * Note: unlike the previous inline logic, this builds a new columns object
 * instead of mutating the existing one in place, so column references change
 * only when their contents change.
 *
 * @param {Array}  rows           the rows to sort (not mutated)
 * @param {Object} columns        current column definitions (not mutated)
 * @param {string} sortedByColumn name of the previously active sort column
 * @param {string} name           name of the column now being sorted
 * @param {string} direction      'ascending' | 'descending'
 * @returns {{ sortedRows: Array, updatedColumns: Object }}
 */
export function sortRowsAndUpdateColumns(
  rows,
  columns,
  sortedByColumn,
  name,
  direction,
) {
  const sortedRows = sortRows(rows, name, direction)

  const updatedColumns = { ...columns }

  updatedColumns[name] = {
    ...updatedColumns[name],
    sortDirection: direction,
    sortButtonLabel: `Currently sorted by ${name} in ${direction} order`,
    isSortCurrent: true,
  }

  // sortedByColumn may not correspond to an actual column key (e.g.
  // DataTableSeeMore's hardcoded default doesn't match every page's column
  // titles) — skip updating metadata for a column that doesn't exist instead
  // of throwing on updatedColumns[sortedByColumn].sortDirection.
  if (
    sortedByColumn &&
    sortedByColumn !== name &&
    updatedColumns[sortedByColumn]
  ) {
    updatedColumns[sortedByColumn] = {
      ...updatedColumns[sortedByColumn],
      sortButtonLabel: `Sorted by ${sortedByColumn} in ${updatedColumns[sortedByColumn].sortDirection} order`,
      isSortCurrent: false,
    }
  }

  return { sortedRows, updatedColumns }
}

/*
    Function: formatApps((raw-json-response-from-developernodeweb))

    reformat data for PayPal UI 2.0 DropdownMenu
*/
export function formatAppOptions(apps, isSandbox) {
  if (!apps) {
    return []
  }

  return apps.map(({ clientid, name }) => ({
    value: !isSandbox ? decryptClientId(clientid) : clientid,
    primaryText: name,
  }))
}

export function formatAccountOptions(accounts) {
  if (!accounts) {
    return []
  }

  return accounts.map(({ primary_email_alias }) => ({
    value: primary_email_alias,
    primaryText: primary_email_alias,
  }))
}

export const formatDate = (worldReady, value) => {
  const date = value instanceof Date ? value : new Date(value)
  const timezone =
    serverData.locality.timezone === 'Europe/Kyiv'
      ? 'Europe/Kiev'
      : serverData.locality.timezone
  return new DateTimeFormat(worldReady, {
    style: 'datetime-short',
    timeZone: timezone,
  }).format(date)
}

/*
    Function: formatEventTypeOptions(raw-json-response-from-developernodeweb)

    UI: PayPal UI 2.0 DropdownMenu

    developernodeweb response format (eventTypesDetails):

        "eventTypesDetails": [
            {
                "eventHeading": "Billing"
            },
            {
                "events": [
                    {
                        "shortDesc": "Plan activated",
                        "value": "BILLING.PLAN.ACTIVATED"
                    },
                    ...
                ]
            }
            ...
        ]

    Expected format:
        [
            {
                value: 'NO.EVENTTYPE',
                primaryText: 'All event types',
            },
            {
                unselectable: true,
                class: 'sectionGroup',
                primaryText: 'Payment',
            },
            {
                value: 'PAYMENT.AUTHORIZATION.CREATED',
                primaryText: 'Authorization created',
            },
            ...
        ]

*/
export function formatEventTypeOptions(eventTypes) {
  if (!eventTypes) {
    return []
  }

  const eventTypeOptions = [
    {
      value: 'NO.EVENTTYPE',
      primaryText: 'All event types',
    },
  ]

  eventTypes.forEach((obj) => {
    if (obj.eventHeading) {
      eventTypeOptions.push({
        unselectable: true,
        primaryText: obj.eventHeading,
      })
    }
    if (obj.events) {
      obj.events.forEach((objInner) => {
        eventTypeOptions.push({
          value: objInner.value,
          primaryText: objInner.shortDesc,
        })
      })
    }
  })

  return eventTypeOptions
}

/*
    Function: formatRows(raw-json-response-from-developernodeweb)

    UI: DashboardTable

    Expected 'DashboardTable' format based on Col format:
        {
            event_id: 'WH-1R202598W01871142-0KN76510EJ8310716',
            event_type: 'Payment sale completed',
            create_time: '20 May 2022 07:12:12',
            summary: 'Payment completed for $ 30.11 USD',
            resource: {
              id: '123456'
            },
            status: 'SUCCESS'
        }
*/
export function formatRows(worldReady, rows) {
  if (!rows) {
    return []
  }

  const formattedRows = []

  rows.forEach((row) => {
    let eventType = row.event_type
    eventType = eventType.toLowerCase().replace(/\b\w/, (c) => c.toUpperCase())
    eventType = eventType.replace(/\./g, ' ')
    // `create_time` must stay the raw epoch so DataTableUtils.sortCompare's
    // plain `<`/`>` comparison sorts chronologically instead of
    // lexicographically sorting a locale-formatted display string.
    const createTimeDisplay = formatDate(worldReady, row.create_time)

    formattedRows.push({
      event_id: row.id,
      event_type: eventType,
      create_time: new Date(row.create_time).getTime(),
      create_time_display: createTimeDisplay,
      summary: row.summary,
      resource_id: row.resource?.id,
      status: row.status,
    })
  })

  return formattedRows
}

/*
  Add a new property to the API Calls row called 'identifier' that is the first element
  of a concatenation of 'tag ids' and 'correlation id'

  Example:
    tag_id: ['PAYID-MMMKK3Y8CT60977U36073457', 'EC-85N32594PX165500M']
    correlation_id: '49a224720aafb'

    Concatentation: ['PAYID-MMMKK3Y8CT60977U36073457', 'EC-85N32594PX165500M', '49a224720aafb']
    identifier: 'PAYID-MMMKK3Y8CT60977U36073457'

*/
export function formatApiCallsRows(worldReady, rows) {
  if (!rows) {
    return []
  }

  return rows.map((obj) => {
    const tagIds = obj.tag_id.toString().split(',')
    const correlationId = obj.correlation_id.toString().split()

    const eventType = `${obj.api_request.method} ${obj.url}`
    // `create_time` must stay the raw epoch so DataTableUtils.sortCompare's
    // plain `<`/`>` comparison sorts chronologically instead of
    // lexicographically sorting a locale-formatted display string.
    const createTimeDisplay = formatDate(worldReady, obj.create_time)

    // concatentate 'tag ids' and 'correlation id', then take the first element as the identifier
    let identifier = tagIds.concat(correlationId)
    // remove any null/blank values
    identifier = identifier.filter((n) => n).shift()

    return {
      ...obj,
      identifier,
      event_type: eventType,
      create_time: new Date(obj.create_time).getTime(),
      create_time_display: createTimeDisplay,
    }
  })
}

/*
  API Call component of Error Log

  Transform API Call response and return
  expected structure for DataTable
*/
export function formatErrorLogRowsApiCalls(worldReady, rows) {
  if (!rows) {
    return []
  }

  return (
    rows
      // skip successful (HTTP 2XX)
      .filter((obj) => !obj.http_status.toString().startsWith('2'))
      // transform to Error Log structure
      .map((obj, index) => {
        const description = obj?.api_response?.body?.message
        const eventType = `${obj?.api_request?.method} ${obj?.url}`
        const id = `row-${index}`
        const errorDate = formatDate(worldReady, obj?.create_time)
        const ts = Number(moment(obj?.create_time).format('x'))

        return {
          id,
          ts,
          // `value` must stay the raw epoch so DataTableUtils.sortCompare's
          // plain `<`/`>` comparison sorts chronologically instead of
          // lexicographically sorting a locale-formatted display string.
          time: { value: ts, displayValue: errorDate },
          status: { value: obj?.http_status },
          debug: { value: obj?.correlation_id, clientId: obj?.client_id },
          event: { value: eventType },
          description: { value: description },
        }
      })
  )
}

/*
  Error Log: Webhooks Events

  Transform Webhooks Events response from this API and return data for Error Log DataTable:

  GET /developer/api/dashboard/webhooks/filter-events
*/
export function formatErrorLogRowsWebhooks(worldReady, clientId, rows) {
  if (!rows) {
    return []
  }

  return (
    rows
      // skip successful (aka non-DELIVERED)
      .filter((obj) => !obj.status.toString().startsWith('SUCCESS'))
      // transform to Error Log structure
      .map((obj, index) => {
        const description = obj.summary
        const eventType = obj.event_type
        const id = `wh-row-${index}`

        // convert from ISO 8601 to formatted:  2022-11-29T15:36:01Z => 29 Nov 2022 09:36:01
        const localDate = formatDate(worldReady, obj.create_time)
        const ts = Number(moment(obj.create_time).format('x'))

        return {
          id,
          ts,
          time: { value: ts, displayValue: localDate },
          status: { value: obj.status },
          debug: {
            value: obj.id,
            eventId: obj.id,
            clientId,
          },
          event: { value: eventType },
          description: { value: description },
        }
      })
  )
}

/*
  Error Log: Webhooks Transmissions

  Transform Webhooks Transmissions response from this API and return data for Error Log DataTable:

  GET https://localhost.paypal.com/developer/api/dashboard/webhooks/transmissions
*/
export function formatErrorLogRowsTransmissions(worldReady, rows) {
  if (!rows?.transmissions?.items) {
    return []
  }

  return (
    rows.transmissions.items
      // skip successful (aka non-DELIVERED)
      .filter((obj) => !obj.status.toString().startsWith('DELIVERED'))
      // transform to Error Log structure
      .map((obj, index) => {
        const eventType = obj.classifiers.pairs['WHE-eventName']
        const clientId = obj.classifiers.pairs['WHE-clientId']
        const id = `wh-row-${index}`

        // convert from ISO 8601 to formatted:  2022-11-29T15:36:01Z => 29 Nov 2022 09:36:01
        const localDate = formatDate(worldReady, obj.creation_time)
        const ts = Number(moment(obj.creation_time).format('x'))

        return {
          id,
          ts,
          time: { value: ts, displayValue: localDate },
          status: { value: obj.status },
          debug: {
            value: obj.classifiers.pairs['WHE-eventId'],
            eventId: obj.classifiers.pairs['WHE-eventId'],
            clientId,
          },
          event: { value: eventType },
          description: { value: obj.description },
        }
      })
  )
}

export function formatNotificationsRows(worldReady, rows) {
  if (!rows) {
    return []
  }

  const formattedRows = []
  rows.forEach((row) => {
    formattedRows.push({
      email_to: row.email_to,
      email_subject: row.email_subject,
      date_received: formatRestApiAppsEpoch(
        worldReady,
        parseInt(row.date_recieved, 10),
      ),
      // date_received: format(
      //   new Date(parseInt(row.date_recieved, 10) * 1000),
      //   'dd MMM yyyy HH:mm:ss',
      // ),
      email_message: row.email_message,
    })
  })
  return formattedRows
}

const formatCurrency = (amount, currency, worldready) => {
  const currencyFormatter = new CurrencyFormat(worldready, { currency })
  return `${currencyFormatter.format(Number(amount))} ${currency}`
}

/**
 * Determines payment method from transaction object
 * @param {Object} obj - Transaction object
 * @returns {string} Payment method string
 */
const getPaymentMethod = (obj) => {
  const fundingSource = obj.extension?.money_movements?.[0]?.funding_source
  if (!fundingSource || fundingSource?.id === 'BALANCE') {
    return obj.counterparty?.email || ''
  }
  if (fundingSource?.last_four_digits) {
    return '•••• ' + fundingSource.last_four_digits
  }
  return ''
}

/**
 * Creates status object from transaction status
 * @param {string} status - Transaction status string
 * @returns {Object} Status object with boolean flags
 */
const createStatusObject = (status) => ({
  isCompleted: status === 'COMPLETED',
  isFailed: status === 'FAILED',
  isPending: status === 'PENDING',
  isHeld: status === 'HELD',
  isUnClaimed: status === 'UNCLAIMED',
  isRefunded: status === 'REFUNDED',
  isPartiallyRefunded: status === 'PARTIALLY_REFUNDED',
})

/*
  Recent Transaction
*/
export function formatRecentTransactionsRows(rows, worldready) {
  if (!rows) {
    return []
  }

  return (
    rows
      // take the first five records
      .slice(0, 5)
      // transform to Recent Transactions structure
      .map((obj, index) => {
        const id = `row-${index}`
        const transactionDate = formatDate(worldready, obj.time_created)
        const paymentMethod = getPaymentMethod(obj)

        return {
          id,
          time: { value: transactionDate },
          status: createStatusObject(obj.status),
          paymentMethod: { value: paymentMethod },
          TransactionId: { value: obj.id },
          amount: {
            value: formatCurrency(
              obj.gross?.value,
              obj.gross?.currency_code,
              worldready,
            ),
          },
        }
      })
  )
}

export function formatRestApiAppsEpoch(worldReady, epochTimestamp) {
  if (
    !epochTimestamp ||
    !Number.isInteger(epochTimestamp) ||
    epochTimestamp < 1000000000
  ) {
    return ''
  }
  const isMilliseconds = epochTimestamp.toString().length === 13
  const epochInSeconds = isMilliseconds ? epochTimestamp / 1000 : epochTimestamp
  const createdDate = moment.unix(epochInSeconds).format('DD MMM YYYY HH:mm:ss')
  return formatDate(worldReady, createdDate)
}

export function calculateExpiryTimeInHours(expiryDate) {
  // Convert expiryDate string to Date object
  const expiryTimeDate = new Date(expiryDate)

  // Calculate time until expiration in milliseconds
  const currentTime = new Date()
  const timeUntilExpiration = expiryTimeDate - currentTime

  // Convert milliseconds to hours and round down to the nearest whole number
  return Math.floor(timeUntilExpiration / (1000 * 60 * 60))
}

export function calculateExpiryTimeInDaysHours(expiryTime) {
  const expiryTimeDate = new Date(expiryTime)

  const currentTime = new Date()
  const timeUntilExpiration = expiryTimeDate - currentTime

  const hoursUntilExpiration = Math.floor(
    timeUntilExpiration / (1000 * 60 * 60),
  )
  const days = Math.floor(hoursUntilExpiration / 24)
  const hours = hoursUntilExpiration % 24

  return `${days} day(s) and ${hours} hour(s)`
}

export function shortenedText(text, maxLength = 25) {
  // maxLength should be at least 8
  if (text.length <= maxLength) {
    return text
  }

  const endLength = 5
  const startLength = Math.max(0, maxLength - 3 - endLength) // 3 dots in between
  const start = text.slice(0, startLength)
  const end = text.slice(-endLength)

  return `${start}...${end}`
}
